// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import authService from '../services/authService';
import { usePermissions } from '../context/PermissionContext';
import './Login.css';

const FEATURES = [
  {
    icon: '📋',
    title: 'Production Logs',
    desc: 'Complete batch records from raw materials to finished goods, always audit-ready.',
  },
  {
    icon: '🔍',
    title: 'Full Traceability',
    desc: 'Instantly trace any ingredient across every production run for compliance and quality.',
  },
  {
    icon: '📦',
    title: 'Inventory Control',
    desc: 'Real-time stock levels, adjustment history, and low-stock visibility at a glance.',
  },
  {
    icon: '📊',
    title: 'Analytics & Insights',
    desc: 'Production trends, quality metrics, and labor data to drive smarter decisions.',
  },
];

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // MFA step
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  const navigate = useNavigate();
  const { tenant } = usePermissions();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await authService.login(username, password, tenant?.companyId ?? null);
      if (result.requiresMfa) {
        setMfaToken(result.mfaToken);
        return;
      }
      // Let RootRedirect (mounted at /redirecting) pick the first module this user actually has
      // access to, once permissions finish loading for the new token — a hardcoded destination
      // would land a restricted tenant/role on an Access Restricted page.
      navigate('/redirecting', { replace: true });
    } catch (error) {
      console.error('Login failed', error);
      setErrorMessage('Invalid username or password. Please try again.');
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    try {
      await authService.verifyMfa(mfaToken, mfaCode);
      navigate('/redirecting', { replace: true });
    } catch (error) {
      setErrorMessage('Invalid authentication code. Please try again.');
      setMfaCode('');
    } finally {
      setMfaLoading(false);
    }
  };

  // Auto-dismiss error after 5s
  useEffect(() => {
    if (!errorMessage) return;
    const timer = setTimeout(() => setErrorMessage(''), 5000);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  return (
    <div className="login-page">

      {/* ── LEFT PANEL ── */}
      <div className="login-left">
        {/* Decorative background blobs */}
        <div className="login-blob login-blob-1" />
        <div className="login-blob login-blob-2" />
        <div className="login-blob login-blob-3" />

        <div className="login-left-content">
          <div className="login-hero">
            <img src="/favicon.svg" alt="quickLOG" className="login-hero-logo" />
            <h1 className="login-hero-name">quickLOG</h1>
            <p className="login-hero-tagline">
              Production intelligence for modern food manufacturers
            </p>
          </div>

          <div className="login-features">
            {FEATURES.map((f, i) => (
              <div key={i} className="login-feature" style={{ animationDelay: `${0.3 + i * 0.12}s` }}>
                <span className="login-feature-icon">{f.icon}</span>
                <div>
                  <div className="login-feature-title">{f.title}</div>
                  <div className="login-feature-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="login-right">
        <div className="login-card">

          {/* Mobile branding (hidden on desktop) */}
          <div className="login-mobile-brand d-lg-none">
            <img src="/favicon.svg" alt="quickLOG" style={{ width: 48, height: 48 }} />
            <span className="login-mobile-name">quickLOG</span>
          </div>

          {errorMessage && (
            <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
              {errorMessage}
              <button type="button" className="btn-close" onClick={() => setErrorMessage('')} />
            </div>
          )}

          {!mfaToken ? (
            <>
              <h2 className="login-card-title">Welcome back</h2>
              <p className="login-card-sub">Sign in to your account</p>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label login-label">Username</label>
                  <input
                    type="text"
                    className="form-control login-input"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="form-label login-label">Password</label>
                  <div className="input-group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control login-input"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword(v => !v)}
                      tabIndex={-1}
                    >
                      {showPassword ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn login-btn w-100">
                  Sign In
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="login-card-title">Two-Factor Auth</h2>
              <p className="login-card-sub">Enter the 6-digit code from your authenticator app.</p>

              <form onSubmit={handleMfaSubmit}>
                <div className="mb-4">
                  <label className="form-label login-label">Authentication Code</label>
                  <input
                    type="text"
                    className="form-control login-input text-center fs-4 fw-bold letter-spacing-wide"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoFocus
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn login-btn w-100"
                  disabled={mfaLoading || mfaCode.length !== 6}
                >
                  {mfaLoading ? <span className="spinner-border spinner-border-sm" /> : 'Verify'}
                </button>
                <button
                  type="button"
                  className="btn btn-link w-100 mt-2"
                  style={{ fontSize: 13, color: '#64748b' }}
                  onClick={() => setMfaToken(null)}
                >
                  Back to login
                </button>
              </form>
            </>
          )}

          <p className="login-footer">
            &copy; {new Date().getFullYear()} quickLOG by{' '}
            <strong>PV Business Consulting, LLC</strong>
            <br />All rights reserved.
            <br />
            <Link to="/privacy" style={{ color: '#94a3b8' }}>Privacy Policy</Link>
            {' · '}
            <Link to="/terms" style={{ color: '#94a3b8' }}>Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
